import { ReactElement, use } from 'react';
import { Controller, Path } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import Checkbox from '@mui/material/Checkbox';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import NumericField from 'shared/components/formFields/numeric-field/numeric-field';

import WanderingBoundaryPassage from 'features/wandering-boundary-settings/domain/models/wandering-boundary-passage';
import WanderingBoundaryPassageRowProviderContext from 'features/wandering-boundary-settings/providers/contexts/wandering-boundary-passage-row-provider-context';
import { BlockingPassageWarningTimeConstants } from 'features/wandering-boundary-settings/providers/states/wandering-boundary-passage-row-provider-state';
import styles from './blocking-passage-warning.module.scss';

interface Props {
  fieldName: Path<WanderingBoundaryPassage>;
}

const BlockingPassageWarning = ({ fieldName }: Readonly<Props>): ReactElement => {
  const { t } = useTranslation('wanderingBoundarySettings');
  const { form, viewingMode, handleBlockingPassageWarningCheckboxChange } = use(WanderingBoundaryPassageRowProviderContext);

  const isViewing = viewingMode == 'viewing';

  const timeValue = form.watch(fieldName);
  const isActive = timeValue !== null && timeValue !== undefined;

  const renderViewingBlockingPassageTime = (): ReactElement => {
    const time = form.getValues(fieldName) as number;
    return (
      <>
        {isActive && time ? (
          <div className={styles.viewBlockingPassageWarning}>
            <Checkbox checked={isActive} disabled />
            <Typography variant="caption">
              {t('wanderingBoundaryPassageFields.blockingPassageWarningTimeReadOnly', { count: time })}
            </Typography>
          </div>
        ) : (
          <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center' }}>
            {t('wanderingBoundaryPassageFields.blockingPassageWarningInactive')}
          </Typography>
        )}
      </>
    );
  };

  const renderCreateOrEditBlockingPassageTime = (): ReactElement => (
    <div className={styles.blockingPassageWarning}>
      <Checkbox checked={isActive} onChange={(_, checked) => handleBlockingPassageWarningCheckboxChange(fieldName, checked)} />
      {isActive ? (
        <Controller
          name={fieldName}
          control={form.control}
          render={({ field, fieldState: { error } }) => (
            <NumericField
              field={field}
              fieldName={fieldName}
              min={BlockingPassageWarningTimeConstants.min}
              max={BlockingPassageWarningTimeConstants.max}
              step={1}
              error={!!error}
              helperText={error?.message?.toString()}
              className={styles.selectField}
              suffix={(value) => t('wanderingBoundaryPassageFields.blockingPassageWarningTime', { count: value })}
            />
          )}
        />
      ) : (
        <TextField
          variant="outlined"
          value={t('wanderingBoundaryPassageFields.blockingPassageWarningInactive')}
          disabled
          className={styles.noDevicesField}
        />
      )}
    </div>
  );

  return (
    <div className={styles.container}>
      <Typography variant="subtitle1">{t('wanderingBoundaryPassageFields.blockingPassageWarningTimeLabel')}</Typography>
      {isViewing ? renderViewingBlockingPassageTime() : renderCreateOrEditBlockingPassageTime()}
    </div>
  );
};

export default BlockingPassageWarning;
